// src/pages/ReportsPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { query, where, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import {
  Card, Table, Button, Container,
  Row, Col
} from "react-bootstrap";
import { format, parseISO } from "date-fns";
import { t } from "../utils/translations.js";
import LoadingSpinner from "../components/common/LoadingSpinner.js";
import EmptyState from "../components/common/EmptyState.js";
import DateRangeFilter from "../components/common/DateRangeFilter.js";
import { useReportFilters } from "../hooks/useReportFilters.js";
import { computeReportMetrics } from "../utils/reportMetrics.js";
import ReportMobileCard from "../components/common/ReportMobileCard.js";

export default function ReportsPage() {
  const { slug, uid: paramUid } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const isAdminView = user?.isAdmin && paramUid;
  const targetUid = isAdminView ? paramUid : user.uid;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const filters = useReportFilters(reports);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const q = query(
        collection(db, "weeklyReports"),
        where("uid", "==", targetUid),
        where("slug", "==", slug)
      );
      const snap = await getDocs(q);
      setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    })();
  }, [user, slug, targetUid]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!reports.length) {
    return (
      <Container className="py-4">
        <EmptyState
          message={`${t(language, "noReportsFor")} "${decodeURIComponent(slug)}"`}
          icon="📋"
        />
        <Button variant="secondary" onClick={() => isAdminView ? navigate('/admin/users') : navigate('/')}>
          ← {t(language, "back")}
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h4>
            {isAdminView
              ? `${t(language, "reportsOf")} ${decodeURIComponent(slug)}`
              : t(language, "myWeeklyReports")}
          </h4>
        </Col>
        <Col className="text-end">
          <Button
            variant="secondary"
            onClick={() =>
              isAdminView
                ? navigate("/admin/users")
                : navigate("/")
            }
          >
            ← {t(language, "back")}
          </Button>
        </Col>
      </Row>

      {/* Filtros e Ordenação */}
      <DateRangeFilter
        startDate={filters.startDate}
        endDate={filters.endDate}
        onStartDateChange={filters.setStartDate}
        onEndDateChange={filters.setEndDate}
        onClear={filters.clearFilters}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortOrder}
      />

      {/* Mensagem se nenhum resultado com filtro */}
      {filters.filteredAndSorted.length === 0 && (filters.startDate || filters.endDate) && (
        <EmptyState
          message={t(language, "noResultsForFilter")}
          icon="ℹ️"
          variant="warning"
        />
      )}

      {filters.filteredAndSorted.length > 0 && (<>
      {/* Desktop Table */}
      <Card className="shadow-sm border-0 overflow-hidden d-none d-md-block">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f4f8', borderBottom: '2px solid #dee2e6' }}>
                <th className="py-3 ps-3">{t(language, "week")}</th>
                <th className="py-3 text-center">{t(language, "totalWorked")}</th>
                <th className="py-3 text-center">{t(language, "totalLunch")}</th>
                <th className="py-3 text-center">{t(language, "totalTravel")}</th>
                <th className="py-3 text-center">{t(language, "totalClosing")}</th>
                <th className="py-3">{t(language, "lastEdit")}</th>
                <th className="py-3 text-center">{t(language, "actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filters.filteredAndSorted.map(r => {
                const dt = parseISO(r.weekStart);
                const weekLabel = format(dt, "dd MMM yyyy");
                const [wH, wM] = (r.weekWorkHours || "00:00").split(":").map(n => +n);
                const totalWorked = `${String(wH).padStart(2, "0")}:${String(wM).padStart(2, "0")}`;
                const lunchMin = Object.values(r.lunchMinDay || {}).reduce((a, b) => a + (b || 0), 0);
                const travelMin = Object.values(r.travelMinDay || {}).reduce((a, b) => a + (b || 0), 0);
                const totalLunch = `${String(Math.floor(lunchMin / 60)).padStart(2, "0")}:${String(lunchMin % 60).padStart(2, "0")}`;
                const totalTravel = `${String(Math.floor(travelMin / 60)).padStart(2, "0")}:${String(travelMin % 60).padStart(2, "0")}`;
                const closingMin = wH * 60 + wM + lunchMin + travelMin;
                const fechamentoTotal = `${String(Math.floor(closingMin / 60)).padStart(2, "0")}:${String(closingMin % 60).padStart(2, "0")}`;
                const timeEdits = (r.history || []).filter(h => h.changes.some(c => c.startsWith("Horas alteradas")));
                const lastHist = timeEdits[timeEdits.length - 1];
                const isExpanded = expandedRows.has(r.id);

                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderLeft: '3px solid #0d6efd' }}>
                      <td className="ps-3">
                        <div className="fw-semibold">{weekLabel}</div>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill" style={{ backgroundColor: '#e7f1ff', color: '#0d6efd', fontSize: '0.85em' }}>
                          {totalWorked}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill" style={{ backgroundColor: '#e0f7fa', color: '#0891b2', fontSize: '0.85em' }}>
                          {totalLunch}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill" style={{ backgroundColor: '#fff3e0', color: '#e65100', fontSize: '0.85em' }}>
                          {totalTravel}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill bg-success" style={{ fontSize: '0.9em', padding: '6px 12px' }}>
                          {fechamentoTotal}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {lastHist ? format(lastHist.when.toDate(), "dd/MM HH:mm") : "-"}
                        </small>
                      </td>
                      <td className="text-center">
                        {timeEdits.length > 0 && (
                          <Button
                            size="sm"
                            variant={isExpanded ? "info" : "outline-info"}
                            className="me-1 px-2 py-1"
                            onClick={() => toggleRow(r.id)}
                            title={t(language, "editHistory")}
                          >
                            {isExpanded ? "▼" : "▶"} {timeEdits.length}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="px-2 py-1"
                          onClick={() =>
                            isAdminView
                              ? navigate(`/admin/users/${paramUid}/colaborador/${slug}?weekStart=${r.weekStart}`)
                              : navigate(`/colaborador/${slug}?weekStart=${r.weekStart}`)
                          }
                        >
                          {t(language, "openEdit")}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && timeEdits.length > 0 && (
                      <tr>
                        <td colSpan={7} className="p-0" style={{ borderLeft: '3px solid #0dcaf0' }}>
                          <div className="bg-light px-4 py-3">
                            <small className="fw-bold d-block mb-2">{t(language, "editHistory")}</small>
                            {timeEdits.map((h, idx) => {
                              const timeChange = h.changes.find(c => c.startsWith("Horas alteradas"));
                              const m = timeChange.match(/Horas alteradas(?: do)? "?(.+?)"? de (\d{2}:\d{2}) para (\d{2}:\d{2})/);
                              const clientName = m ? m[1] : "";
                              const oldTime = m ? m[2] : "";
                              const newTime = m ? m[3] : "";
                              return (
                                <div key={idx} className="d-flex gap-3 small mb-1 text-muted">
                                  <span>{format(h.when.toDate(), "dd/MM HH:mm")}</span>
                                  <span className="fw-semibold">{h.user}</span>
                                  <span>"{clientName}" {oldTime} → {newTime}</span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
        <Card.Footer className="bg-light text-muted small py-2 px-3">
          {filters.filteredAndSorted.length} relatório{filters.filteredAndSorted.length !== 1 ? 's' : ''}
        </Card.Footer>
      </Card>

      {/* Mobile Cards */}
      <div className="d-md-none">
        {filters.filteredAndSorted.map(r => {
          const m = computeReportMetrics(r);
          const weekLabel = format(parseISO(r.weekStart), "dd MMM yyyy");
          const timeEdits = (r.history || []).filter(h => h.changes.some(c => c.startsWith("Horas alteradas")));
          const lastHist = timeEdits[timeEdits.length - 1];
          const isExpanded = expandedRows.has(r.id);
          return (
            <ReportMobileCard
              key={r.id}
              weekLabel={weekLabel}
              metrics={m}
              lastEdit={lastHist ? format(lastHist.when.toDate(), "dd/MM HH:mm") : ""}
              bottomContent={isExpanded && timeEdits.length > 0 ? (
                <div className="mt-2 pt-2 border-top">
                  <small className="fw-bold d-block mb-1">{t(language, "editHistory")}</small>
                  {timeEdits.map((h, idx) => {
                    const tc = h.changes.find(c => c.startsWith("Horas alteradas"));
                    const match = tc.match(/Horas alteradas(?: do)? "?(.+?)"? de (\d{2}:\d{2}) para (\d{2}:\d{2})/);
                    return (
                      <div key={idx} className="small text-muted mb-1">
                        {format(h.when.toDate(), "dd/MM HH:mm")}{" "}
                        <span className="fw-semibold">{h.user}</span>{" "}
                        {match && <span>"{match[1]}" {match[2]} &rarr; {match[3]}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            >
              {timeEdits.length > 0 && (
                <Button size="sm" variant={isExpanded ? "info" : "outline-info"} className="px-2 py-1" onClick={() => toggleRow(r.id)}>
                  {isExpanded ? "\u25BC" : "\u25B6"} {timeEdits.length}
                </Button>
              )}
              <Button size="sm" variant="outline-primary" className="px-2 py-1"
                onClick={() => isAdminView
                  ? navigate(`/admin/users/${paramUid}/colaborador/${slug}?weekStart=${r.weekStart}`)
                  : navigate(`/colaborador/${slug}?weekStart=${r.weekStart}`)
                }
              >
                {t(language, "openEdit")}
              </Button>
            </ReportMobileCard>
          );
        })}
        <div className="text-muted small text-center py-2">
          {filters.filteredAndSorted.length} relatório{filters.filteredAndSorted.length !== 1 ? 's' : ''}
        </div>
      </div>
      </>)}
    </Container>
  );
}
